import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

// Tiny stale-while-revalidate cache for public, infrequently-changing data
// (services, therapists, cms content). Prevents page-transition flicker:
//   - first component that asks triggers the network fetch
//   - next component mounts within TTL → gets cached data synchronously,
//     then a background revalidation refreshes it (next reader sees new data).
// Stored in memory for instant reads + sessionStorage so a hard refresh within
// the same tab still benefits.

// TTL matches backend `Cache-Control: max-age=30`. Keeping these in sync means
// a CMS edit reliably propagates to public visitors within ~30 seconds, while
// repeat clicks within the same minute still feel instant.
const DEFAULT_TTL_MS = 30 * 1000; // 30 seconds
const memory = new Map();
const inflight = new Map();

function readPersistent(key) {
  try {
    const raw = sessionStorage.getItem(`pc:${key}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.ts !== "number") return null;
    return parsed;
  } catch { return null; }
}

function writePersistent(key, entry) {
  try { sessionStorage.setItem(`pc:${key}`, JSON.stringify(entry)); } catch { /* quota */ }
}

/**
 * Fetch with SWR semantics. Returns a promise that resolves to either:
 *   - cached data immediately (if fresh or stale — stale triggers a bg refresh)
 *   - fresh data if no cache exists
 *
 * @param {string} key           Cache key (e.g. "services")
 * @param {string} path          Backend path after /api (e.g. "/services")
 * @param {object} [options]
 * @param {number} [options.ttl] Freshness window in ms. Default 2 min.
 * @param {object} [options.params] Query params forwarded to axios.
 */
export async function fetchCached(key, path, { ttl = DEFAULT_TTL_MS, params } = {}) {
  const now = Date.now();
  const cacheKey = params ? `${key}?${new URLSearchParams(params).toString()}` : key;

  // 1. in-memory hit
  const mem = memory.get(cacheKey);
  if (mem) {
    const age = now - mem.ts;
    if (age < ttl) return mem.data;                          // fresh — return directly
    // stale — return immediately, revalidate in background
    revalidate(cacheKey, path, params).catch(() => {});
    return mem.data;
  }

  // 2. sessionStorage hit (survives component unmount / page nav)
  const persistent = readPersistent(cacheKey);
  if (persistent) {
    memory.set(cacheKey, persistent);
    const age = now - persistent.ts;
    if (age < ttl) {
      // revalidate in background so next mount sees fresh data
      revalidate(cacheKey, path, params).catch(() => {});
      return persistent.data;
    }
    // stale — return immediately, revalidate in background
    revalidate(cacheKey, path, params).catch(() => {});
    return persistent.data;
  }

  // 3. cold — single in-flight request (dedupes concurrent callers)
  return revalidate(cacheKey, path, params);
}

async function revalidate(cacheKey, path, params) {
  const existing = inflight.get(cacheKey);
  if (existing) return existing;
  const p = axios.get(`${API}${path}`, { params }).then((res) => {
    const entry = { ts: Date.now(), data: res.data };
    memory.set(cacheKey, entry);
    writePersistent(cacheKey, entry);
    return res.data;
  }).finally(() => { inflight.delete(cacheKey); });
  inflight.set(cacheKey, p);
  return p;
}

/** Invalidate a single key (e.g. after admin edits a service). */
export function invalidateCached(key) {
  for (const k of Array.from(memory.keys())) {
    if (k === key || k.startsWith(`${key}?`)) memory.delete(k);
  }
  try {
    for (let i = sessionStorage.length - 1; i >= 0; i--) {
      const sk = sessionStorage.key(i);
      if (sk && (sk === `pc:${key}` || sk.startsWith(`pc:${key}?`))) sessionStorage.removeItem(sk);
    }
  } catch { /* ignore */ }
}
